import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, lastValueFrom } from 'rxjs'
import { environment } from '../../environments/environment'
import { ResponseStatus } from '../models/ResponseStatus'

/** The features opened to the signed-in restaurant, from its row of restaurant_features. */
export interface RestaurantFeatures {
  emails: boolean
  campaigns: boolean
}

export type RestaurantFeature = keyof RestaurantFeatures

const NO_FEATURES: RestaurantFeatures = { emails: false, campaigns: false }

@Injectable({
  providedIn: 'root',
})
export class FeatureAccessService {
  private mergeLogsAccessChecked = false
  private mergeLogsAccessSubject = new BehaviorSubject<boolean | null>(null)

  public mergeLogsAccess$ = this.mergeLogsAccessSubject.asObservable()

  private restaurantFeaturesRequest: Promise<RestaurantFeatures> | null = null
  private restaurantFeaturesSubject =
    new BehaviorSubject<RestaurantFeatures | null>(null)

  /** Null until they have been asked for since signing in. */
  public restaurantFeatures$ = this.restaurantFeaturesSubject.asObservable()

  /**
   * The restaurant's features, asked for once per sign-in and shared by the menu and the
   * route guards. A failed request opens nothing, as a feature is off until granted.
   */
  restaurantFeatures(): Promise<RestaurantFeatures> {
    this.restaurantFeaturesRequest ??= lastValueFrom(
      this.http.get<RestaurantFeatures>(`${environment.apiUrl}/features`, {
        withCredentials: true,
      }),
    )
      .catch(() => NO_FEATURES)
      .then((features) => {
        this.restaurantFeaturesSubject.next(features)
        return features
      })
    return this.restaurantFeaturesRequest
  }

  async hasFeature(feature: RestaurantFeature): Promise<boolean> {
    return (await this.restaurantFeatures())[feature]
  }

  constructor(private http: HttpClient) {}

  async canAccessMergeLogs(): Promise<boolean> {
    if (this.mergeLogsAccessChecked) {
      return this.mergeLogsAccessSubject.value ?? false
    }

    const url = `${environment.apiUrl}/auth/validate_feature`

    try {
      const resp = this.http.get<ResponseStatus>(url, { withCredentials: true })
      const result = await lastValueFrom<ResponseStatus>(resp)
      const hasAccess = result.status
      this.mergeLogsAccessSubject.next(hasAccess)
      this.mergeLogsAccessChecked = true
      return hasAccess
    } catch {
      this.mergeLogsAccessSubject.next(false)
      this.mergeLogsAccessChecked = true
      return false
    }
  }

  resetAccessCheck(): void {
    this.mergeLogsAccessChecked = false
    this.mergeLogsAccessSubject.next(null)
    this.restaurantFeaturesRequest = null
    this.restaurantFeaturesSubject.next(null)
  }
}
